import {
    Download,
    Show as FileOpenIcon,
    FolderOpen,
    Headphone,
    LinkExternal,
} from "@styled-icons/boxicons-regular";
import { File, Video } from "@styled-icons/boxicons-solid";
import { observer } from "mobx-react-lite";
import { isFirefox } from "react-device-detect";
import { API } from "revolt.js";
import styles from "./AttachmentActions.module.scss";
import classNames from "classnames";
import { useEffect } from "preact/hooks";

import { IconButton } from "@revoltchat/ui";

import { determineFileSizeSI } from "../../../../lib/fileSize";
import { useTranslation } from "../../../../lib/i18n";

import downloadStore from "../../../../mobx/stores/Download";

import { useClient } from "../../../../controllers/client/ClientController";
import { modalController } from "../../../../controllers/modals/ModalController";
import Tooltip from "../../../common/Tooltip";

const isDesktop =
    window.isNative &&
    window.native.downloadAttachmentToDefaultPath !== undefined &&
    window.native.saveAttachmentAs !== undefined;

interface Props {
    attachment: API.File;
}

function AttachmentActionsInner({ attachment }: Props) {
    const client = useClient();
    const { filename, metadata, size, _id } = attachment;

    const url = client.generateFileURL(attachment);
    const full_url = `${url}/${encodeURIComponent(filename)}`;
    const download_url = url;

    const filesize = determineFileSizeSI(size);

    const translate = useTranslation();

    const downloadState = downloadStore.get(_id);
    const isDownloading = downloadState?.status.status === "downloading";
    const isDownloaded = downloadState?.status.status === "downloaded";

    useEffect(() => {
        if (!isDesktop) return;
        downloadStore.checkStatus(_id);
        return downloadStore.onError(({ attachment_id, error }) => {
            if (attachment_id === _id) {
                modalController.push({
                    type: "download_error",
                    attachment_id: _id,
                    error,
                });
            }
        });
    }, []);

    const handleOpen = async () => {
        if (!isDesktop) return;
        const status = await downloadStore.checkStatus(_id);
        if (status?.status === "downloaded") {
            window.native.openAttachment(_id);
        } else if (status?.status === "downloading") {
            return;
        } else {
            downloadStore.requestOpen(_id);
            window.native.downloadAttachmentToDefaultPath(
                full_url,
                _id,
                filename,
            );
        }
    };

    const handleSaveAs = () => {
        window.native.saveAttachmentAs(full_url, _id, filename);
    };

    const handleShowInFolder = async () => {
        if (!isDesktop) return;
        const status = await downloadStore.checkStatus(_id);
        if (status?.status === "downloaded") {
            window.native.showAttachmentInFolder(_id);
        } else if (status?.status === "downloading") {
            return;
        } else {
            downloadStore.requestShowInFolder(_id);
            window.native.downloadAttachmentToDefaultPath(
                full_url,
                _id,
                filename,
            );
        }
    };

    const makeAttachmentControls = () => {
        if (isDownloaded) {
            return (
                <div onClick={handleShowInFolder}>
                    <IconButton>
                        <Tooltip
                            content={translate(
                                "app.context_menu.show_in_folder",
                            )}>
                            <FolderOpen size={24} />
                        </Tooltip>
                    </IconButton>
                </div>
            );
        }

        if (isDownloading) {
            const progress = downloadState?.progress ?? 0;
            return (
                <div className={styles.downloadProgress}>
                    <div
                        className={styles.downloadProgressFill}
                        style={{ width: `${progress * 100}%` }}
                    />
                    <span className={styles.downloadProgressText}>
                        {Math.round(progress * 100)}%
                    </span>
                </div>
            );
        }

        // Not downloaded
        return (
            <div onClick={handleSaveAs}>
                <IconButton>
                    <Tooltip content={translate("app.context_menu.save_as")}>
                        <Download size={24} />
                    </Tooltip>
                </IconButton>
            </div>
        );
    };

    if (isDesktop) {
        switch (metadata.type) {
            case "Image":
                return (
                    <div
                        className={classNames(
                            styles.actions,
                            styles.imageAction,
                        )}>
                        <span>{filename}</span>
                        <span className={styles.filesize}>
                            {`${metadata.width}x${metadata.height}`} ({filesize}
                            )
                        </span>
                        <Tooltip
                            content={translate("app.context_menu.open_image")}>
                            <IconButton onClick={handleOpen}>
                                <FileOpenIcon size={24} />
                            </IconButton>
                        </Tooltip>
                        {makeAttachmentControls()}
                    </div>
                );
            case "Audio":
                return (
                    <div
                        className={classNames(
                            styles.actions,
                            styles.audioAction,
                        )}>
                        <Headphone size={24} className={styles.iconType} />
                        <span>{filename}</span>
                        <span className={styles.filesize}>{filesize}</span>
                        {makeAttachmentControls()}
                    </div>
                );
            case "Video":
                return (
                    <div
                        className={classNames(
                            styles.actions,
                            styles.videoAction,
                        )}>
                        <Video size={24} className={styles.iconType} />
                        <span>{filename}</span>
                        <span className={styles.filesize}>
                            {`${metadata.width}x${metadata.height}`} ({filesize}
                            )
                        </span>
                        {makeAttachmentControls()}
                    </div>
                );
            case "Text":
                return (
                    <div className={styles.actions}>
                        <File size={24} className={styles.iconType} />
                        <div onClick={handleOpen} style="cursor: pointer;">
                            <span>{filename}</span>
                        </div>
                        <span className={styles.filesize}>{filesize}</span>
                        {makeAttachmentControls()}
                    </div>
                );
            default:
                return (
                    <div className={styles.actions}>
                        <File size={24} className={styles.iconType} />
                        <div onClick={handleOpen} style="cursor: pointer;">
                            <Tooltip content={filename}>
                                <span>{filename}</span>
                            </Tooltip>
                        </div>
                        <span className={styles.filesize}>{filesize}</span>
                        {makeAttachmentControls()}
                    </div>
                );
        }
    } else {
        switch (metadata.type) {
            case "Image":
                return (
                    <div
                        className={classNames(
                            styles.actions,
                            styles.imageAction,
                        )}>
                        <span>{filename}</span>
                        <span className={styles.filesize}>
                            {`${metadata.width}x${metadata.height}`} ({filesize}
                            )
                        </span>
                        <a
                            target="_blank"
                            href={full_url}
                            className={styles.iconType}
                            rel="noreferrer">
                            <IconButton>
                                <LinkExternal size={24} />
                            </IconButton>
                        </a>
                        <a
                            target="_blank"
                            href={full_url}
                            className={styles.downloadIcon}
                            download
                            rel="noreferrer">
                            <IconButton>
                                <Download size={24} />
                            </IconButton>
                        </a>
                    </div>
                );
            case "Audio":
                return (
                    <div
                        className={classNames(
                            styles.actions,
                            styles.audioAction,
                        )}>
                        <Headphone size={24} className={styles.iconType} />
                        <span>{filename}</span>
                        <span className={styles.filesize}>{filesize}</span>
                        <a
                            href={download_url}
                            className={styles.downloadIcon}
                            download
                            target={isFirefox ? "_blank" : "_self"}
                            rel="noreferrer">
                            <IconButton>
                                <Download size={24} />
                            </IconButton>
                        </a>
                    </div>
                );
            case "Video":
                return (
                    <div
                        className={classNames(
                            styles.actions,
                            styles.videoAction,
                        )}>
                        <Video size={24} className={styles.iconType} />
                        <span>{filename}</span>
                        <span className={styles.filesize}>
                            {`${metadata.width}x${metadata.height}`} ({filesize}
                            )
                        </span>
                        <a
                            href={download_url}
                            className={styles.downloadIcon}
                            download
                            target={isFirefox ? "_blank" : "_self"}
                            rel="noreferrer">
                            <IconButton>
                                <Download size={24} />
                            </IconButton>
                        </a>
                    </div>
                );
            case "Text":
                return (
                    <div className={styles.actions}>
                        <File size={24} className={styles.iconType} />
                        <span>{filename}</span>
                        <span className={styles.filesize}>{filesize}</span>
                        <a
                            href={full_url}
                            target="_blank"
                            className={styles.externalType}
                            rel="noreferrer">
                            <IconButton>
                                <LinkExternal size={24} />
                            </IconButton>
                        </a>
                        <a
                            href={download_url}
                            className={styles.downloadIcon}
                            download
                            target={isFirefox ? "_blank" : "_self"}
                            rel="noreferrer">
                            <IconButton>
                                <Download size={24} />
                            </IconButton>
                        </a>
                    </div>
                );
            default:
                return (
                    <div className={styles.actions}>
                        <File size={24} className={styles.iconType} />
                        <Tooltip content={filename}>
                            <span>{filename}</span>
                        </Tooltip>
                        <span className={styles.filesize}>{filesize}</span>
                        <a
                            href={download_url}
                            className={styles.downloadIcon}
                            download
                            target={isFirefox ? "_blank" : "_self"}
                            rel="noreferrer">
                            <IconButton>
                                <Download size={24} />
                            </IconButton>
                        </a>
                    </div>
                );
        }
    }
}

export default observer(AttachmentActionsInner);
